import { Component, NgZone, OnDestroy } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { addDays, addHours, addMinutes, format } from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Jobs } from 'server/shared/collections/jobs';
import { Users } from 'server/shared/collections/users';
import {
  canBookInviteeForJob,
  isAllowedOnJob,
} from 'server/shared/helpers/canBookInviteeForJob';
import {
  checkIfRested,
  getFirstShift,
  getLastShift,
  isCurrentlyEmployed,
  RestedResult,
} from 'server/shared/helpers/eligibilityChecks';
import { inviteeFitsJobCriteria } from 'server/shared/helpers/inviteeFitsJobCriteria';
import { addTimeZoneOffset } from 'server/shared/helpers/removeTimeZoneOffset';
import { militaryTimeToHourAndMinutes } from 'server/shared/helpers/time';
import { AvailabilityStatusEnum } from 'server/shared/models/availabilityStatusEnum';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { PoolerType } from 'server/shared/models/poolerType';
import { RoleEnum } from 'server/shared/models/role.enum';
import { Shift } from 'server/shared/models/shift';
import {
  DispoUser,
  MappedCertificate,
  PoolerUser,
} from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { UserRestWarningTypeEnum } from 'server/shared/models/userRestWarning';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { getJobRestWarning } from '../../helpers/getRestWarning';
import { processBookingWarnings } from '../../helpers/job/processBookingWarnings';
import { rxNgZoneRun } from '../../helpers/rxNgZoneRun';
import { CertificateService } from '../services/certificate.service';
import { CustomerService } from '../services/customer.service';
import { OrganisationService } from '../services/organisation.service';
import { PoolerService } from '../services/pooler/pooler.service';
import { ToastService } from '../services/toast/toast.service';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';
import { SubscriptionService } from '../shared/subscription.service';

interface PlanningMatrixPooler extends PoolerUser {
  userAvailability: UserAvailability;
}

type MatrixUserRow = {
  user: PlanningMatrixPooler;
  restWarningUser: PlanningMatrixPooler;
  detailsCollapsed: boolean;
  customerOfUser?: {
    id: string;
    displayName: string;
  };
  availabilityNote?: string;
  hasBeenSelected?: boolean;
};

type MatrixJobUser = {
  status:
    | InviteeStatusEnum.AVAILABLE
    | InviteeStatusEnum.UNAVAILABLE
    | InviteeStatusEnum.BOOKED
    | InviteeStatusEnum.WORKING;
  from: Date;
  to: Date;
  restWarning?: { restStatus: RestedResult; restWarning?: string };
};

type MatrixDepartmentJob = {
  id: string;
  status: {
    inviteeStatus:
      | InviteeStatusEnum.AVAILABLE
      | InviteeStatusEnum.UNAVAILABLE
      | InviteeStatusEnum.BOOKED;
    inviteeUserId?: string;
  }[];
  usersById: Map<string, MatrixJobUser>;
  shifts: Shift[];
  requiredUsers: number;
};

type MatrixOrganisationRow = {
  id: string;
  name: string;
  collapsed: boolean;
  departments: { id: string; name: string; jobs: MatrixDepartmentJob[] }[];
};

type SelectedAvailability = {
  jobID: string;
  userID: string;
  shifts: Shift[];
  blockedFrom: Date;
  blockedUntil: Date;
  customer: {
    id: string;
    displayName: string;
  };
};

@Component({
  selector: 'planning-matrix',
  templateUrl: 'planning-matrix.html',
  styleUrls: ['planning-matrix.scss', 'planning-matrix-custom-table.scss'],
})
export class PlanningMatrixComponent implements OnDestroy {
  i18n = this.translationService.getTranslations('planning-matrix');
  loggedInUser?: DispoUser;
  destroyed$ = new Subject<void>();

  selectedDate: string = format(addDays(new Date(), 1), dbDayDateToken);
  today = new Date();

  inviteeStatusEnum = InviteeStatusEnum;

  inProgress: boolean = false;

  selectedAvailabilitiesCount: number = 0;
  selectedAvailabilities$: BehaviorSubject<
    Map<string, SelectedAvailability[]>
  > = new BehaviorSubject<Map<string, SelectedAvailability[]>>(
    new Map<string, SelectedAvailability[]>(),
  );

  dateQueryParams$ = this.route.queryParams.pipe(
    map((params): string => {
      let param = params['date'];

      this.selectedDate = param;
      return param;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  certificatesQueryParams$: Observable<MappedCertificate[]> = combineLatest([
    this.route.queryParams,
    this.certificateService.readyCertificates$,
  ]).pipe(
    map(([params, certificates]): MappedCertificate[] => {
      let param = params['certificates'];
      let certificateIds = param?.split(',') || [];

      return certificates
        ?.filter((certificate) => certificateIds.includes(certificate._id))
        ?.map((certificate) => {
          return { id: certificate._id, displayName: certificate.name };
        });
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  demandReasonQueryParam$: Observable<string | undefined> = combineLatest([
    this.route.queryParams,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([params, customer]): string | undefined => {
      let param = params['demandReason'];
      if (
        !param ||
        param === 'ALL' ||
        (!customer?.enableDemandReasons && !customer?.enableJobRequestProcess)
      ) {
        return undefined;
      }
      return param;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  jobsState$ = combineLatest([
    this.dateQueryParams$,
    this.certificatesQueryParams$,
    this.demandReasonQueryParam$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    switchMap(([dateString, certificates, demandReasonId, customer]) => {
      let query: Mongo.Selector<Job> = {
        date: dateString,
        status: JobStatusEnum.OPEN,
      };
      if (certificates.length) {
        query['$or'] = [
          {
            'certificates.id': {
              $in: certificates.map((certificate) => certificate.id),
            },
          },
          { 'certificates.0': { $exists: false } },
        ];
      }
      if (demandReasonId && customer?._id) {
        const customerIdKey = `customerInternalInformation.${customer._id}.demandReason.id`;
        query[customerIdKey] = demandReasonId;
      }

      return new Observable<{ jobs: Job[]; loading: boolean }>((subscriber) => {
        subscriber.next({ jobs: [], loading: true });
        let innerSub: Subscription | undefined;
        const timeoutId = Meteor.setTimeout(() => {
          innerSub = Jobs.find(query)
            .pipe(map((jobs) => ({ jobs, loading: false })))
            .subscribe({
              next: (val) => subscriber.next(val),
              error: (err) => subscriber.error(err),
              complete: () => subscriber.complete(),
            });
        }, 0);
        return () => {
          Meteor.clearTimeout(timeoutId);
          innerSub?.unsubscribe();
        };
      });
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  jobs$: Observable<Job[]> = this.jobsState$.pipe(
    map((state) => state.jobs),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  jobsLoading$ = this.jobsState$.pipe(
    map((state) => state.loading),
    startWith(true),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  users$: Observable<PoolerUser[]> = combineLatest([
    this.dateQueryParams$,
    this.poolerService.poolerUsers$,
    this.certificatesQueryParams$,
  ]).pipe(
    map(([dateString, users, certificates]) => {
      return users.filter((user) =>
        user.certificates?.some(
          (userCertificate) =>
            certificates.some(
              (queryCertificate) => queryCertificate.id === userCertificate.id,
            ) || !certificates.length,
        ),
      );
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  availableUsers$ = combineLatest([
    this.dateQueryParams$,
    this.users$,
    this.jobs$,
    this.customerService.readyDispoCustomer$,
    this.poolerService.readyPoolerCustomerMap$,
    this.organisationService.readyDepartments$,
  ]).pipe(
    map(([date, users, jobs, customer, poolerMap, departments]) => {
      let userRows: MatrixUserRow[] = [];

      users.forEach((user) => {
        const customerOfPooler = poolerMap?.getCustomerOfUser(user._id);
        if (!customerOfPooler) {
          return;
        }
        let userAvailability = user.availabilities.find(
          (availability) => availability.date === date,
        );
        let userRow: MatrixUserRow = {
          user: { ...user, userAvailability } as PlanningMatrixPooler,
          restWarningUser: JSON.parse(
            JSON.stringify({ ...user, userAvailability }),
          ),
          detailsCollapsed: true,
        };
        if (customerOfPooler._id !== customer._id) {
          userRow.customerOfUser = {
            id: customerOfPooler._id,
            displayName: customerOfPooler.name,
          };
        }

        if (userAvailability?.note) {
          userRow.availabilityNote = userAvailability.note;
        }

        let hasAvailableJob = false;

        jobs.forEach((job) => {
          let department = departments.find(
            (department) => department.id === job.department.id,
          );
          if (!department) {
            return;
          }
          if (
            this.userAvailableOnJob(
              user,
              job,
              customerOfPooler,
              department,
              userAvailability,
            )
          ) {
            hasAvailableJob = true;
          }
        });

        // Do not add row if all columns are negative
        if (!hasAvailableJob) {
          return;
        }
        userRows.push(userRow);
      });
      return userRows;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  organisationRows$ = combineLatest([
    this.jobs$,
    this.availableUsers$,
    this.poolerService.readyPoolerCustomerMap$,
    this.selectedAvailabilities$,
    this.customerService.readyDispoCustomer$,
    this.organisationService.readyDepartments$,
  ]).pipe(
    map(
      ([
        jobs,
        users,
        poolerMap,
        selectedAvailabilities,
        customer,
        departments,
      ]) => {
        let organisationRows: MatrixOrganisationRow[] = [];

        jobs.forEach((job) => {
          let jobRow: MatrixDepartmentJob = {
            id: job._id,
            status: [],
            usersById: new Map(),
            shifts: job.shifts,
            requiredUsers:
              job.requiredUsers -
                job.invitees.filter(
                  (invitee) => invitee.status === InviteeStatusEnum.BOOKED,
                ).length || 0,
          };

          let selectedJobAvailabilities = selectedAvailabilities.get(job._id);

          let bookedUserIds: string[] = [];
          let availableUserCount = 0;

          users.forEach((userRow) => {
            let user = userRow.user;
            let restWarningUser = userRow.restWarningUser;
            let customerOfPooler = poolerMap?.getCustomerOfUser(user._id);
            if (!customerOfPooler) {
              return;
            }
            let selectedUserAvailabilities = selectedAvailabilities.get(
              user._id,
            );

            userRow.hasBeenSelected = !!selectedUserAvailabilities?.length;
            let restWarning:
              | { restStatus: RestedResult; restWarning?: string }
              | undefined;
            if (this.i18n) {
              restWarning = getJobRestWarning(
                {
                  date: job.date,
                  shifts: job.shifts,
                  jobId: job._id,
                  invitees: job.invitees,
                },
                restWarningUser,
                this.i18n,
              );
            }

            let restStatus = checkIfRested({
              user: restWarningUser,
              jobDate: job.date,
              jobShifts: job.shifts,
              requiredPause: customer.requiredPause || 1,
            });

            let fromHours = militaryTimeToHourAndMinutes(
              getFirstShift(job.shifts).from,
            );
            let untilHours = militaryTimeToHourAndMinutes(
              getLastShift(job.shifts).to,
            );

            let blockedFrom = addTimeZoneOffset(
              addMinutes(
                addHours(new Date(job.date), fromHours.hours),
                fromHours.minutes,
              ),
            );
            let blockedUntil = addTimeZoneOffset(
              addMinutes(
                addHours(new Date(job.date), untilHours.hours),
                untilHours.minutes,
              ),
            );

            if (blockedFrom > blockedUntil) {
              blockedUntil = addDays(blockedUntil, 1);
            }

            let department = departments.find(
              (department) => department.id === job.department.id,
            );
            if (!department) {
              return;
            }

            if (
              !this.userAvailableOnJob(
                user,
                job,
                customerOfPooler,
                department,
                user.userAvailability,
              )
            ) {
              jobRow.usersById.set(user._id, {
                status: InviteeStatusEnum.UNAVAILABLE,
                from: blockedFrom,
                to: blockedUntil,
                restWarning,
              });
            } else {
              if (
                selectedJobAvailabilities?.some(
                  (selectedJobAvailability) =>
                    selectedJobAvailability.userID === user._id,
                )
              ) {
                bookedUserIds.push(user._id);
                jobRow.usersById.set(user._id, {
                  status: InviteeStatusEnum.BOOKED,
                  from: blockedFrom,
                  to: blockedUntil,
                  restWarning,
                });
              } else if (
                (selectedJobAvailabilities?.length || 0) >= jobRow.requiredUsers
              ) {
                jobRow.usersById.set(user._id, {
                  status: InviteeStatusEnum.WORKING,
                  from: blockedFrom,
                  to: blockedUntil,
                  restWarning,
                });
              } else if (
                selectedUserAvailabilities?.length &&
                !restStatus?.hasRested
              ) {
                jobRow.usersById.set(user._id, {
                  status: InviteeStatusEnum.WORKING,
                  from: blockedFrom,
                  to: blockedUntil,
                  restWarning,
                });
              } else {
                availableUserCount++;
                jobRow.usersById.set(user._id, {
                  status: InviteeStatusEnum.AVAILABLE,
                  from: blockedFrom,
                  to: blockedUntil,
                  restWarning,
                });
              }
            }
          });

          for (let i = 0; i < jobRow.requiredUsers; i++) {
            if (bookedUserIds[i]) {
              jobRow.status.push({
                inviteeStatus: InviteeStatusEnum.BOOKED,
                inviteeUserId: bookedUserIds[i],
              });
            } else if (availableUserCount > i - bookedUserIds.length) {
              jobRow.status.push({
                inviteeStatus: InviteeStatusEnum.AVAILABLE,
              });
            } else {
              jobRow.status.push({
                inviteeStatus: InviteeStatusEnum.UNAVAILABLE,
              });
            }
          }

          if (
            organisationRows.some((organisation) =>
              organisation.departments.some(
                (dep) => dep.id === job.department.id,
              ),
            )
          ) {
            let organisation = organisationRows.find(
              (organisation) => organisation.id === job.organisation.id,
            );
            let department = organisation?.departments.find(
              (dep) => dep.id === job.department.id,
            );
            if (department) {
              department.jobs.push(jobRow);
            }
          } else if (
            organisationRows.some(
              (organisation) => organisation.id === job.organisation.id,
            )
          ) {
            let organisation = organisationRows.find(
              (organisation) => organisation.id === job.organisation.id,
            );
            if (organisation) {
              organisation?.departments.push({
                id: job.department.id,
                name: job.department.name,
                jobs: [jobRow],
              });
            }
          } else {
            organisationRows.push({
              id: job.organisation.id,
              name: job.organisation.name,
              collapsed: false,
              departments: [
                {
                  id: job.department.id,
                  name: job.department.name,
                  jobs: [jobRow],
                },
              ],
            });
          }
        });

        return organisationRows;
      },
    ),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  constructor(
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private userService: UserService,
    private customerService: CustomerService,
    private certificateService: CertificateService,
    private poolerService: PoolerService,
    private subscriptionService: SubscriptionService,
    private organisationService: OrganisationService,
    private dialog: MatDialog,
  ) {
    this.subscriptionService.jobUsersReady$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    this.subscriptionService.jobs.ready$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    this.userService.readyUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        if (user.role !== RoleEnum.DISPO) {
          this.router.navigate(['/']).then();
          return;
        }
        this.loggedInUser = user;
      });

    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        if (!customer?.enablePlanningMatrix) {
          this.router.navigate(['/']).then();
        }
      });

    if (!this.route.snapshot.queryParams['date']) {
      let dateString = format(addDays(new Date(), 1), dbDayDateToken);
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: { date: dateString },
          queryParamsHandling: 'merge',
        })
        .then();
    }
  }

  selectDate(event: MatDatepickerInputEvent<any, string>) {
    if (event.value) {
      let dateString = format(new Date(event.value), dbDayDateToken);

      this.selectedDate = dateString;
      this.ngZone.run(() => {
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: { date: dateString },
            queryParamsHandling: 'merge',
          })
          .then();
      });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async selectAvailability(jobRow: MatrixDepartmentJob, user: PoolerUser) {
    let userById = jobRow.usersById.get(user._id);
    if (
      !userById ||
      userById.status === InviteeStatusEnum.UNAVAILABLE ||
      userById.status === InviteeStatusEnum.WORKING
    ) {
      return;
    }
    let selectedAvailabilities = this.selectedAvailabilities$.getValue();
    let existingJobAvailabilities = selectedAvailabilities.get(jobRow.id);
    let existingUserAvailabilities = selectedAvailabilities.get(user._id);
    if (
      existingJobAvailabilities?.find(
        (availability) => availability.userID === user._id,
      ) ||
      existingUserAvailabilities?.find(
        (availability) => availability.jobID === jobRow.id,
      )
    ) {
      selectedAvailabilities.set(
        jobRow.id,
        selectedAvailabilities
          .get(jobRow.id)
          ?.filter((availability) => availability.userID !== user._id) || [],
      );
      selectedAvailabilities.set(
        user._id,
        selectedAvailabilities
          .get(user._id)
          ?.filter((availability) => availability.jobID !== jobRow.id) || [],
      );

      let restWarningIndex = user.restWarnings?.findIndex(
        (restWarning) =>
          restWarning.type === UserRestWarningTypeEnum.ELEVEN_HOUR_WARNING &&
          restWarning.bookedJob.jobId === jobRow.id,
      );
      if (restWarningIndex !== undefined) {
        user.restWarnings?.splice(restWarningIndex, 1);
      }
    } else {
      const customerOfPooler = await this.poolerService.getCustomerOfPooler(
        user._id,
      );
      if (
        (existingJobAvailabilities?.length || 0) >= jobRow.requiredUsers ||
        !customerOfPooler
      ) {
        return;
      }
      let selectedAvailability = {
        jobID: jobRow.id,
        userID: user._id,
        shifts: jobRow.shifts,
        blockedFrom: userById.from,
        blockedUntil: userById.to,
        customer: {
          id: customerOfPooler._id,
          displayName: customerOfPooler.name,
        },
      };

      selectedAvailabilities.set(jobRow.id, [
        ...(existingJobAvailabilities || []),
        selectedAvailability,
      ]);
      selectedAvailabilities.set(user._id, [
        ...(existingUserAvailabilities || []),
        selectedAvailability,
      ]);

      if (!user.restWarnings?.length) {
        user.restWarnings = [];
      }
      user.restWarnings.push({
        type: UserRestWarningTypeEnum.ELEVEN_HOUR_WARNING,
        jobDate: this.selectedDate,
        affectedDates: [this.selectedDate],
        bookedJob: {
          jobId: jobRow.id,
          bookedFrom: userById.from,
          bookedUntil: userById.to,
        },
      });
    }

    this.selectedAvailabilitiesCount = this.getTotalSelectedAvailabilities(
      selectedAvailabilities,
    ).length;
    this.selectedAvailabilities$.next(selectedAvailabilities);
  }

  resetSelectedAvailabilities() {
    this.selectedAvailabilities$.next(
      new Map<string, SelectedAvailability[]>(),
    );
    this.selectedAvailabilitiesCount = 0;
  }

  async submitSelectedAvailabilities(
    selectedAvailabilities: Map<string, SelectedAvailability[]>,
  ) {
    if (this.inProgress) return;
    this.inProgress = true;
    let totalSelectedAvailabilities = this.getTotalSelectedAvailabilities(
      selectedAvailabilities,
    );
    if (!totalSelectedAvailabilities.length) {
      this.inProgress = false;
      return;
    }

    // Check for booking warnings before submitting
    const loggedInCustomer = await firstValueFrom(
      this.customerService.readyDispoCustomer$,
    );
    if (!loggedInCustomer) {
      this.inProgress = false;
      return;
    }

    // Collect all users that will be booked
    const usersToBook = totalSelectedAvailabilities
      .map((availability) => {
        const user = Users.findOne(availability.userID) as PoolerUser;
        if (!user) {
          return null;
        }
        const poolerType = loggedInCustomer.poolerTypes?.find(
          (poolerType) =>
            poolerType.id ===
            user?.customerInformation?.[availability.customer.id]?.poolerType
              ?.id,
        );
        return {
          user: user,
          poolerType: poolerType,
        };
      })
      .filter(
        (item): item is { user: PoolerUser; poolerType: PoolerType } =>
          item !== null,
      ) as { user: PoolerUser; poolerType?: PoolerType }[];

    const allConfirmed = await processBookingWarnings(
      usersToBook,
      this.dialog,
      this.i18n,
    );

    if (!allConfirmed) {
      this.inProgress = false;
      return;
    }

    Meteor.call(
      'planningMatrixBooking',
      totalSelectedAvailabilities,
      allConfirmed,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'jobSaved' });
          this.selectedAvailabilities$.next(
            new Map<string, SelectedAvailability[]>(),
          );
          this.selectedAvailabilitiesCount = 0;
        }
        this.inProgress = false;
      },
    );
  }

  userAvailableOnJob(
    user: PoolerUser,
    job: Job,
    customer: Customer,
    department: Department,
    userAvailability?: UserAvailability,
  ): boolean {
    let preventDirectAcceptWithinSecondsPerShift = 0;
    if (
      department.customerInformation?.[customer._id]
        ?.preventDirectAcceptWithinSecondsPerShift?.[job.shifts[0].shift] !==
      undefined
    ) {
      preventDirectAcceptWithinSecondsPerShift =
        department.customerInformation?.[customer._id]
          ?.preventDirectAcceptWithinSecondsPerShift?.[job.shifts[0].shift] ||
        0;
    } else if (
      customer.preventDirectAcceptWithinSecondsPerShift?.[job.shifts[0].shift]
    ) {
      preventDirectAcceptWithinSecondsPerShift =
        customer.preventDirectAcceptWithinSecondsPerShift?.[
          job.shifts[0].shift
        ] || 0;
    }

    let timeBeforeBookable = addHours(
      new Date(),
      preventDirectAcceptWithinSecondsPerShift / 3600,
    );

    return !!(
      job.jobStart > timeBeforeBookable &&
      ((user.shifts?.some((userShift) =>
        job.shifts.some((jobShift) => jobShift.shift === userShift),
      ) &&
        job.shifts.some(
          (shift) =>
            userAvailability?.shifts?.[shift.shift] ===
            AvailabilityStatusEnum.AVAILABLE,
        )) ||
        job.invitees.some(
          (invitee) =>
            invitee.userId === user._id &&
            invitee.status === InviteeStatusEnum.AVAILABLE,
        )) &&
      checkIfRested({
        user,
        jobDate: job.date,
        jobShifts: job.shifts,
        requiredPause: customer.requiredPause,
        blockingOnly: true,
      })?.hasRested &&
      canBookInviteeForJob(user, job).canBook &&
      isAllowedOnJob(user, job) &&
      inviteeFitsJobCriteria(user, job) &&
      !job.invitees?.some(
        (invitee) =>
          invitee.userId === user._id &&
          invitee.status === InviteeStatusEnum.UNAVAILABLE,
      ) &&
      isCurrentlyEmployed(user, customer._id, job.date)
    );
  }

  getTotalSelectedAvailabilities(
    selectedAvailabilities: Map<string, SelectedAvailability[]>,
  ): SelectedAvailability[] {
    let selectedAvailabilitiesArray: SelectedAvailability[] = [];
    for (let [key, availabilities] of selectedAvailabilities) {
      selectedAvailabilitiesArray.push(...availabilities);
    }
    let selectedAvailabilitiesSet = new Set(selectedAvailabilitiesArray);

    return Array.from(selectedAvailabilitiesSet);
  }
}
